import React, { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom';
import Modal from 'react-modal';
import { Auth } from 'aws-amplify';
import { getOrganizations, getUserDetails } from './services/api/userAPI';
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";
import { setCurrentUser, setFirstCollection } from './state/features/user/userSlice';
import MainLayout from './components/Layout/MainLayout/MainLayout';
import { ErrorBoundary } from '@sentry/react'; // Import Sentry's ErrorBoundary
import * as Sentry from "@sentry/react";
import {
  ROUTE_FETCH_EVENTS, ROUTE_LOGIN, ROUTE_PAGE_NOT_FOUND,
  ROUTE_PASSWORD_RESET, ROUTE_ONBOARDING,
  ROUTE_FETCH_ROOT, ROUTE_INDEX_ROOT, ROUTE_ECHO_ROOT, ROUTE_PROFILE,
  ROUTE_NOTIFICATION_COMMENTS, ROUTE_SETTINGS, ROUTE_ORGANIZATION_SETTINGS, ROUTE_FETCH_CHAT_ROOM, ROUTE_ACCESS_DENIED,
  ROUTE_WEBSOCKET_TEST, ROUTE_UNIFIED_AGENT_TEST
} from './constants/routes';
import { clearBreadcrumbPages } from './state/features/breadcrumb/breadcrumbSlice';
import { clearFetchEventsState } from './state/features/fetchEvents/fetchEventsSlice';
import Color from 'color';
import { getFirstCollection } from './services/api-service';
import { getRouteAfterRemovingPostfix, getRouteAfterRemovingPrefix, isPublicRoute } from './utils/routeHelpers';
import awsconfig from './aws-exports';
import { getAmplifySessionHeaders } from "./services/auth-service";
import { API_URL } from "./config";
import useRoute from './hooks/useRoute';
import { UserModesEnum } from './enums/user';
import 'react-toastify/dist/ReactToastify.css';
import { setUserId, trackPageView } from './utils/tracking';
import { setAllOrganizations, setSelectedOrganization } from './state/features/organization/organizationSlice';
import AgentFetch from "./pages/AgentFetch/AgentFetch";
import { ToastContainer } from 'react-toastify';
import { checkIsBreadcrumbEnabledByLocationPathname } from './utils/breadcrumbHelpers';
import ChatBotWidget from './components/ChatBotWidget/ChatBotWidget';

const Profile = lazy(() => import('./pages/Profile/Profile'));
const NotificationComments = lazy(() => import('./pages/NotificationComments/NotificationComments'));
const SettingsPage = lazy(() => import('./pages/SettingsPage/SettingsPage'));
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings/OrganizationSettings'))
const ChatRoom = lazy(() => import('./pages/AgentFetch/ChatRoom'));
const PageNotFound = lazy(() => import('./pages/PageNotFound/PageNotFound'));
const AccessDeniedPage = lazy(() => import('./pages/AccessDeniedPage/AccessDeniedPage'));
const LoginPage = lazy(() => import('./pages/LoginPage/LoginPage'));
const PasswordReset = lazy(() => import('./pages/PasswordReset/PasswordReset'));
const AgentIndex = lazy(() => import('./pages/AgentIndex/AgentIndex'));
const AgentEcho = lazy(() => import('./pages/AgentEcho/AgentEcho'));
const Onboarding = lazy(() => import('./pages/Onboarding/Onboarding'));
const WebSocketTest = lazy(() => import('./pages/WebSocketTest/WebSocketTest'));
const UnifiedAgentTest = lazy(() => import('./pages/UnifiedAgentTest/UnifiedAgentTest'));


Modal.setAppElement('#root');
Auth.configure(awsconfig);

// Utility to wait for Amplify session and token
const waitForAmplifySession = async (maxWaitMs = 3000, intervalMs = 100) => {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      if (token) return session;
    } catch (e) {
      // Session not ready yet
    }
    await new Promise(res => setTimeout(res, intervalMs));
  }
  throw new Error('Amplify session not ready after waiting');
};

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { getRoute } = useRoute();
  const currentLoggedInUser = useAppSelector((state) => state.user.currentUser);
  const breadcrumbPages = useAppSelector((state) => state.breadcrumb.breadcrumbPages);
  const fetchEventsSelectedDateRange = useAppSelector((state) => state.fetchEvents.selectedDateRange);
  const fetchEventsSelectedSubCategory = useAppSelector((state) => state.fetchEvents.selectedEventSubCategoryName);
  const searchparams = new URLSearchParams(window.location.search);
  const newMode = searchparams.get('mode');

  const newOrgId = window.location.pathname.split('/')[2];

  const primaryColor = process.env.REACT_APP_PRIMARY_COLOR || "#39B88D";
  const secondaryColor = process.env.REACT_APP_SECONDARY_COLOR || "#1d2345";
  const tertColor = process.env.REACT_APP_TERT_COLOR || secondaryColor;
  const loginTextColor = process.env.REACT_APP_LOGIN_TEXT_COLOR || '#475569';
  const chatIcon = process.env.REACT_APP_CHAT_ICON || 'https://public-styling-assets.s3.amazonaws.com/cucopilot/SensoLogo-1x.png';


  const allOrganizations = useAppSelector((state) => state.organization);

  useEffect(() => {
    if (currentLoggedInUser?.user?.user_id) {
      setUserId(currentLoggedInUser.user.user_id, allOrganizations);
    }
  }, [currentLoggedInUser, allOrganizations]);


  useEffect(() => {
    trackPageView(document.title, location.pathname, window.location.href);
     /*
        Note: To track the Senso app's navigation history count, we had to manually
        increment the page navigation count since there was no 
        built-in solution available in React Router or browser-level APIs.
    */
    sessionStorage.setItem('navigationCount', Number(sessionStorage.getItem('navigationCount')) + 1 );
  }, [location.pathname]);

  useEffect(() => {
    // Get the domain name
    const domain = window.location.hostname;

    let title;
    switch (domain) {
      case 'platform.cucopilot.com':
        title = 'CUCopilot';
        break;
      case 'fundmore.senso.ai':
        title = 'FundMore';
        break;
      case 'platform.senso.ai':
        title = 'Senso Platform';
        break;
      case 'truassist.trustone.org':
        title = 'TruAssist';
        break;
      case 'tribalscale.senso.ai':
        title = 'TribalScale';
        break;
      case 'developer.senso.ai':
        title = 'Senso Developer Network';
        break;
      case 'beta.senso.ai':
        title = 'Senso Platform (Beta)';
        break;
      case 'localhost':
        title = 'Senso Platform (Local)';
        break;
      default:
        title = 'CUCopilot'; // Set a default title if domain doesn't match
    }

    // Set document title
    document.title = title;
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', primaryColor);
    document.documentElement.style.setProperty('--lighter-primary-color', Color(primaryColor).lightness(90).hex());
    document.documentElement.style.setProperty('--transparent-primary-color', Color(primaryColor).alpha(0.25));
    document.documentElement.style.setProperty('--secondary-color', secondaryColor);
    document.documentElement.style.setProperty('--tert-color', tertColor);
    document.documentElement.style.setProperty('--login-text-color', loginTextColor);
    document.documentElement.style.setProperty('--chat-icon', `url(${chatIcon})`);
  }, [primaryColor, secondaryColor, tertColor, loginTextColor, chatIcon]);

  const setFavicon = (faviconUrl) => {
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = faviconUrl;
    document.getElementsByTagName('head')[0].appendChild(link);
  };

  useEffect(() => {
    const faviconUrl = process.env.REACT_APP_FAVICON_URL;
    if (faviconUrl) {
      setFavicon(faviconUrl);
    }
  }, []);

  const fetchOrgsAndFirstCollection = async () => {
    const headers = await getAmplifySessionHeaders();
    try {
      const response = await fetch(`${API_URL}/users/orgs/`, {
        method: "GET",
        headers: headers
      });
      const data = await response.json();
      dispatch(setAllOrganizations(data));

      const currentOrg = data.find((org) => org.current);
      if (currentOrg) {
        dispatch(setSelectedOrganization(currentOrg))
      }

      const firstCollection = await getFirstCollection();
      dispatch(setFirstCollection(firstCollection));

    } catch (error) {
      console.error('Error fetching organizations', error);
    }
  };

  const checkUserSession = async () => {
    const currentPath = `${window.location.pathname}${window.location.search}`;
    console.log("🔍 checkUserSession called with path:", currentPath);
    
    try {
      console.log("🔍 Attempting to get Auth.currentSession()");
      await Auth.currentSession();
      
      if (!currentLoggedInUser) {
        console.log("🔍 No currentLoggedInUser, fetching user details");
        const fetchedUser = await getUserDetails();
        console.log("🔍 User details fetched:", fetchedUser.user.email);
        
        const cognitoUser = await Auth.currentAuthenticatedUser();
        Sentry.setUser({
          id: cognitoUser.attributes.sub,
          email: fetchedUser.user.email,
        });

        if (!!location.state?.isOnboardingSuccess) {
          console.log("🔍 isOnboardingSuccess path - navigating to profile");
          navigate(ROUTE_PROFILE.replace(':organization_id', fetchedUser.current_org.org_id).replace(':mode_id', (fetchedUser.ui_settings.is_org_mode ? UserModesEnum.ORG_LEVEL : UserModesEnum.NERWORK_LEVEL).toString()), { state: { isOnboardingSuccess: true }, replace: true });
          dispatch(setCurrentUser(fetchedUser));
          await fetchOrgsAndFirstCollection();
        } else if (newOrgId && newMode) {
          console.log("🔍 newOrgId and newMode path - validating and switching");
          await validateAndSwitchModeAndOrg(fetchedUser);
        } else {
          console.log("🔍 Default path - navigating to chat room");
          console.log("🔍 Current path before navigation:", window.location.pathname);
          dispatch(setCurrentUser(fetchedUser));
          await fetchOrgsAndFirstCollection();
          
          // This is likely the problematic navigation
          console.log("🔍 About to navigate to ROUTE_FETCH_CHAT_ROOM");
          const chatRoomRoute = ROUTE_FETCH_CHAT_ROOM
            .replace(':organization_id', fetchedUser.current_org.org_id)
            .replace(':mode_id', fetchedUser.ui_settings.is_org_mode ? UserModesEnum.ORG_LEVEL : UserModesEnum.NERWORK_LEVEL);
          console.log("🔍 Navigating to:", chatRoomRoute);
          navigate(chatRoomRoute);
        }
      } else {
        console.log("🔍 currentLoggedInUser already exists, skipping fetch");
      }
    } catch (error) {
      console.log("🔍 Auth.currentSession() error:", error.message || error);
      if (!isPublicRoute(currentPath)) {
        console.log("🔍 Not a public route, redirecting to login");
        sessionStorage.setItem('currentPath', JSON.stringify(currentPath));
        navigate(ROUTE_LOGIN);
      } else {
        console.log("🔍 Public route, no redirect needed");
      }
    }
  };

  useEffect(() => {
    console.log("🔍 App.js useEffect triggered for location.pathname:", location.pathname);
    console.log("🔍 isPublicRoute check:", isPublicRoute(location.pathname));

    if (!isPublicRoute(location.pathname)) {
      console.log("🔍 Not a public route, calling checkUserSession");
      checkUserSession();
    } else {
      console.log("🔍 Public route, skipping checkUserSession");
    }

    if (!checkIsBreadcrumbEnabledByLocationPathname(location.pathname) && breadcrumbPages.length > 0) {
      dispatch(clearBreadcrumbPages());
    }

    if ((fetchEventsSelectedDateRange || fetchEventsSelectedSubCategory) &&
      !location.pathname.includes(getRouteAfterRemovingPostfix(getRouteAfterRemovingPrefix(ROUTE_FETCH_EVENTS)))) {
      dispatch(clearFetchEventsState());
    }
  }, [location.pathname]);

  const handleChangeMode = async () => {
    const headers = await getAmplifySessionHeaders();
    const reqBody = { mode: newMode };

    await fetch(`${API_URL}/users/mode/`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(
        reqBody
      ),
    });
  }

  const handleOrgChange = async (orgId) => {
    const headers = await getAmplifySessionHeaders();
    try {
      await fetch(`${API_URL}/users/orgs/`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify({
          org_id: orgId,
        }),
      });
    } catch (error) {
      // redirect to not found page
      navigate(getRoute(ROUTE_PAGE_NOT_FOUND));
      console.error('Error changing organization', error);
    }
  };

  const validateAndSwitchModeAndOrg = async (fetchedUser) => {

    const currentMode = (fetchedUser.ui_settings.is_org_mode ? UserModesEnum.ORG_LEVEL : UserModesEnum.NERWORK_LEVEL).toString();
    const currentOrgId = fetchedUser.current_org.org_id;

    if (currentOrgId === newOrgId && currentMode === newMode) {
      dispatch(setCurrentUser(fetchedUser));
      await fetchOrgsAndFirstCollection();
    } else {
      if (currentOrgId !== newOrgId) {
        const response = await getOrganizations();
        if (response) {
          const isUserBelongsToNewOrg = !!response.data.find((org) => org.org_id === newOrgId);
          if (isUserBelongsToNewOrg) {
            await handleOrgChange(newOrgId);
          } else {
            navigate(ROUTE_ACCESS_DENIED);
            return;
          }
        }
      }
      if (currentMode.toString() !== newMode.toString()) {
        await handleChangeMode();
      }
      const newFetchedUser = await getUserDetails();
      dispatch(setCurrentUser(newFetchedUser));
      await fetchOrgsAndFirstCollection();
    }
  }

  if (!isPublicRoute(window.location.pathname) && !currentLoggedInUser) {
    return null;
  }

  return (
    <ErrorBoundary fallback={<div>An unexpected error has occurred, please refresh the page.</div>}>
      <div className="mainAppContainer">
        <ToastContainer />
        {currentLoggedInUser && <ChatBotWidget />}
        <Routes>
          <Route element={<MainLayout />}>
            <Route path={`/${getRouteAfterRemovingPostfix(ROUTE_PROFILE)}`} element={
              <Suspense fallback={<></>}>
                <Profile key={location.key} />
              </Suspense>
            } />
            <Route path={`/${getRouteAfterRemovingPostfix(ROUTE_NOTIFICATION_COMMENTS)}`} element={
              <Suspense fallback={<></>}>
                <NotificationComments key={location.key} />
              </Suspense>
            } />
            <Route exact path={`/${getRouteAfterRemovingPostfix(ROUTE_SETTINGS)}`} element={
              <Suspense fallback={<></>}>
                <SettingsPage />
              </Suspense>
            } />
            <Route exact path={`/${getRouteAfterRemovingPostfix(ROUTE_ORGANIZATION_SETTINGS)}`} element={
              currentLoggedInUser && currentLoggedInUser.current_org.is_admin ?
                <Suspense fallback={<></>}>
                  <OrganizationSettings key={location.key} />
                </Suspense>
                :
                <Navigate to={getRoute(ROUTE_ACCESS_DENIED)} replace />
            } />
            <Route path={`${ROUTE_ECHO_ROOT}/*`} element={
              <Suspense fallback={<></>}>
                <AgentEcho />
              </Suspense>
            } />
            <Route path={`/${ROUTE_FETCH_ROOT}/*`} element={
              <Suspense fallback={<></>}>
                <AgentFetch />
              </Suspense>
            } />
            <Route path={`/${ROUTE_INDEX_ROOT}/*`} element={
              <Suspense fallback={<></>}>
                <AgentIndex />
              </Suspense>
            } />
            <Route exact path="/chat" element={
              <Suspense fallback={<></>}>
                <ChatRoom />
              </Suspense>
            } />
            <Route exact path={ROUTE_PAGE_NOT_FOUND} element={
              <Suspense fallback={<></>}>
                <PageNotFound />
              </Suspense>
            } />
          </Route>
          <Route exact path={ROUTE_LOGIN} element={
            <Suspense fallback={<></>}>
              <LoginPage />
            </Suspense>
          } />
          <Route exact path={ROUTE_PASSWORD_RESET} element={
            <Suspense fallback={<></>}>
              <PasswordReset />
            </Suspense>
          } />
          <Route exact path={ROUTE_ONBOARDING} element={
            <Suspense fallback={<></>}>
              <Onboarding />
            </Suspense>
          } />
          <Route exact path={ROUTE_ACCESS_DENIED} element={
            <Suspense fallback={<></>}>
              <AccessDeniedPage />
            </Suspense>
          } />
          <Route exact path={ROUTE_WEBSOCKET_TEST} element={
            <Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <p>Loading WebSocket Test Page...</p>
              </div>
            }>
              {/* Public route - no authentication required */}
              <WebSocketTest />
            </Suspense>
          } />
          <Route exact path={ROUTE_UNIFIED_AGENT_TEST} element={
            <Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <p>Loading Unified Agent Test Page...</p>
              </div>
            }>
              <UnifiedAgentTest />
            </Suspense>
          } />
        </Routes>
      </div>
    </ErrorBoundary>
  );
};

export default App;
